import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, tap } from 'rxjs';

import {
  TellentAssistantMastraStreamClient,
  type CurrentThreadMessages,
} from '@core/tellent-assistant-data';
import type { ConversationStarterMessage } from '@core/tellent-assistant-types';
import { toMastraSystemMessage } from '@core/tellent-assistant-util';
import { generateUuid } from '@core/utils-legacy';

import { TellentAssistantThreadsService } from '../tellent-assistant-threads.service';
import { AssistantRunStore } from './assistant-run.store';

@Injectable({ providedIn: 'root' })
export class AssistantRunService {
  private readonly store = inject(AssistantRunStore);
  private readonly client = inject(TellentAssistantMastraStreamClient);
  private readonly threadsService = inject(TellentAssistantThreadsService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly messages = this.store.messages;
  public readonly runStatus = this.store.runStatus;
  public readonly currentThreadId = this.store.currentThreadId;
  public readonly lastError = this.store.lastError;
  public readonly isRunning = this.store.isRunning;

  constructor() {
    this.client.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(msgs => this.store.replaceMessages([...msgs]));
    this.client.runError$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(err => this.store.markRunError(err.message));
    this.client.runFinalized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.store.runStatus() === 'running') this.store.markRunEnd();
      this.threadsService.loadThreads();
    });

    const syncFromCurrentThread = rxMethod<CurrentThreadMessages | null>(
      (source$: Observable<CurrentThreadMessages | null>) =>
        source$.pipe(
          tap((t: CurrentThreadMessages | null) => {
            if (t === null || t.threadId !== this.store.currentThreadId()) return;
            this.client.setMessages(t.messages);
          }),
        ),
    );
    syncFromCurrentThread(toObservable(this.threadsService.currentThread));
  }

  public send(text: string): void {
    this.sendMessages([{ role: 'user', content: text }]);
  }

  public sendMessages(messages: readonly ConversationStarterMessage[]): void {
    if (!this.store.currentThreadId()) {
      const id = generateUuid();
      this.startNewThread(id);
    }
    const inputMessages = messages.map(message =>
      message.role === 'user'
        ? this.client.addUserMessage(message.content)
        : toMastraSystemMessage(message.content),
    );
    this.store.markRunStart();
    void this.client.run(generateUuid(), inputMessages);
  }

  public cancelRun(): void {
    this.client.cancelRun();
    if (this.store.runStatus() === 'running') this.store.markRunEnd();
  }

  public resetThread(): void {
    this.cancelRun();
    this.store.resetForThread(null);
    this.client.setMessages([]);
  }

  public startNewThread(threadId: string): void {
    this.store.resetForThread(threadId);
    this.client.setThreadId(threadId);
    this.client.setMessages([]);
    this.client.markThreadAsNew();
  }

  public switchToExistingThread(threadId: string): void {
    this.store.resetForThread(threadId);
    this.client.setThreadId(threadId);
    this.client.setMessages([]);
  }
}
